/**
 * FieldWrapper - Base wrapper component for form fields
 *
 * Task: T-P4-D2-F4b-4
 * Feature: P4-D2-F4 Web App Forms UI
 *
 * Provides consistent structure for all field types:
 * - Label with optional required indicator
 * - Help text below label
 * - Children (the actual input)
 * - Footer with error message and/or character counter
 *
 * Animation delay is controlled by parent via style prop.
 */

import type { ReactNode, CSSProperties } from 'react';
import { useFieldPart } from './fieldParts';
import { ErrorIcon } from './Icons';
import styles from './fields.module.css';

export interface FieldWrapperProps {
  /** Unique field ID for accessibility */
  fieldId: string;
  /** Field label text */
  label: string;
  /** Whether field is required */
  required?: boolean;
  /** Optional help text below label */
  helpText?: string;
  /** Error message to display */
  error?: string;
  /** The input element(s) */
  children: ReactNode;
  /** Max character length (for counter display) */
  maxLength?: number;
  /** Current character count */
  currentLength?: number;
  /** Custom style for animation delay */
  style?: CSSProperties;
  /**
   * Whether this wraps a group of inputs (radio/checkbox group).
   * When true, uses id instead of htmlFor (since there's no single input to link to).
   * The group should use aria-labelledby={`${fieldId}-label`} to reference this label.
   */
  isGroup?: boolean;
}

/**
 * FieldWrapper wraps all field types with consistent label, help, error, and counter.
 */
export function FieldWrapper({
  fieldId,
  label,
  required,
  helpText,
  error,
  children,
  maxLength,
  currentLength,
  style,
  isGroup = false,
}: FieldWrapperProps) {
  const part = useFieldPart();
  // Determine counter color class
  const getCounterClass = () => {
    if (!maxLength || currentLength === undefined) return '';
    if (currentLength >= maxLength) return styles.atLimit;
    if (currentLength >= maxLength * 0.8) return styles.nearLimit;
    return '';
  };

  // For groups (radio/checkbox), use id instead of htmlFor
  // The group uses aria-labelledby to reference this label
  const labelId = `${fieldId}-label`;

  return (
    <div className={styles.fieldGroup} data-field-id={fieldId} style={style} {...part('question')}>
      {/* Label - for groups, use id; for single inputs, use htmlFor */}
      {label && (
        <label
          className={styles.label}
          {...(isGroup ? { id: labelId } : { htmlFor: fieldId })}
          {...part('label')}
        >
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      {/* Help text */}
      {helpText && <span className={styles.helpText} {...part('hint')}>{helpText}</span>}

      {/* Input element(s) */}
      {children}

      {/* Footer: error + counter */}
      <div className={styles.fieldFooter}>
        {error && (
          <span
            className={styles.errorText}
            id={`${fieldId}-error`}
            role="alert"
          >
            <ErrorIcon />
            {error}
          </span>
        )}
        {maxLength !== undefined && (
          <span className={`${styles.counter} ${getCounterClass()}`}>
            {currentLength ?? 0} / {maxLength}
          </span>
        )}
      </div>
    </div>
  );
}
